import React from "react";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";

const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Grid container sx={{ py: 0.6 }} alignItems="flex-start">
      <Grid item xs={5} md={4}>
        <Typography
          style={{
            fontSize: "13px",
            color: "#888",
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={7} md={8}>
        <Typography
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#1b2838",
            lineHeight: 1.6,
          }}
        >
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
};

const Section = ({ title, step, goToStep, children, isIta }) => (
  <Box
    sx={{
      mb: 2,
      borderRadius: "12px",
      border: "1px solid #e8e8e8",
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 1.5,
        backgroundColor: "#f9f9f9",
        borderBottom: "1px solid #e8e8e8",
      }}
    >
      <Typography
        style={{
          fontWeight: 700,
          fontSize: "16px",
          letterSpacing: "0.06em",
          color: "#1b2838",
        }}
      >
        {title}
      </Typography>
      {goToStep && (
        <Button
          onClick={() => goToStep(step)}
          className="button-bg-empty"
          sx={{
            height: "34px",
            minWidth: "120px !important",
            width: "120px !important",
            borderRadius: "10px !important",
            border: "1px solid #1b2838 !important",
            display: "flex",
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <div className="icon-edit-step" />
          <Typography
            style={{
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "initial",
              color: "#1b2838",
            }}
          >
            {isIta ? "Modifica" : "Edit"}
          </Typography>
        </Button>
      )}
    </Box>
    <Box sx={{ px: 3, py: 2 }}>{children}</Box>
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mt: 2 }}>
    <Typography
      style={{
        fontSize: "14px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: "#1b2838",
        marginBottom: "6px",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const Summary_Aml_Fisico = ({ aml, languange, goToStep }) => {
  const isIta = languange !== "ENG";
  const t = (ita, eng) => (languange === "ENG" ? eng : ita);

  const beneficialOwners = aml?.beneficial_owners || [];

  return (
    <Grid container style={{ display: "flex", justifyContent: "center" }}>
      <Grid item xs={12} md={10}>
        <Box sx={{ mb: 3 }}>
          <Typography
            className="color-blue-dark"
            style={{
              fontSize: "24px",
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            {isIta ? "Riepilogo dati inseriti" : "Inserted data summary"}
          </Typography>
        </Box>

        {/* ── CLIENTE ── */}
        <Section
          title={t("Cliente", "Client")}
          goToStep={goToStep}
          step={1}
          isIta={isIta}
        >
          <SubSection
            title={t(
              "Dati anagrafici e professione/attività",
              "Identification data and occupation/activity",
            )}
          >
            <Row label={t("Nome", "Name")} value={aml?.client?.first_name} />
            <Row
              label={t("Cognome", "Lastname")}
              value={aml?.client?.last_name}
            />
            <Row
              label={t("Luogo di nascita", "Place of birth")}
              value={aml?.client?.birth_place}
            />
            <Row
              label={t("Data di nascita", "Date of birth")}
              value={aml?.client?.birth_date}
            />
            <Row
              label={t("Codice fiscale", "Tax ID number")}
              value={aml?.client?.tax_code}
            />
            <Row
              label={t("Professione/Attività", "Occupation/Activity")}
              value={aml?.client?.profession}
            />
          </SubSection>

          <Divider sx={{ my: 1.5 }} />
          <SubSection title={t("Residenza", "Residence")}>
            <Row
              label={t("Indirizzo", "Address")}
              value={aml?.client?.residence?.address}
            />
            <Row
              label={t("CAP", "ZIP code")}
              value={aml?.client?.residence?.zip}
            />
            <Row
              label={t("Città", "City")}
              value={aml?.client?.residence?.city}
            />
            <Row
              label={t("Provincia", "Province")}
              value={aml?.client?.residence?.province}
            />
            <Row
              label={t("Nazione", "Nation")}
              value={aml?.client?.residence?.country}
            />
          </SubSection>

          {aml?.client?.domicile_same === false && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <SubSection title={t("Domicilio", "Domicile")}>
                <Row
                  label={t("Indirizzo", "Address")}
                  value={aml?.client?.domicile?.address}
                />
                <Row
                  label={t("CAP", "ZIP code")}
                  value={aml?.client?.domicile?.zip}
                />
                <Row
                  label={t("Città", "City")}
                  value={aml?.client?.domicile?.city}
                />
                <Row
                  label={t("Provincia", "Province")}
                  value={aml?.client?.domicile?.province}
                />
                <Row
                  label={t("Nazione", "Nation")}
                  value={aml?.client?.domicile?.country}
                />
              </SubSection>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />
          <SubSection
            title={t(
              "Recapiti per la corrispondenza elettronica",
              "Contact details for electronic correspondence",
            )}
          >
            <Row
              label={t("Indirizzo PEC", "PEC address")}
              value={aml?.client?.pec}
            />
            <Row
              label={t("Indirizzo e-mail", "E-mail address")}
              value={aml?.client?.email}
            />
          </SubSection>

          <Divider sx={{ my: 1.5 }} />
          <SubSection
            title={t(
              "Estremi del documento di identificazione",
              "Data of the identification document",
            )}
          >
            <Row
              label={t("Tipo documento", "Document type")}
              value={aml?.client?.document?.type}
            />
            <Row
              label={t("Numero documento", "Document number")}
              value={aml?.client?.document?.number}
            />
            <Row
              label={t("Ente rilascio", "Issuing body")}
              value={aml?.client?.document?.issuer}
            />
            <Row
              label={t("Data rilascio", "Issuing date")}
              value={aml?.client?.document?.issue_date}
            />
            <Row
              label={t("Data scadenza", "Expiration date")}
              value={aml?.client?.document?.expiry_date}
            />
          </SubSection>

          <Divider sx={{ my: 1.5 }} />
          <SubSection
            title={t(
              "Persona politicamente esposta",
              "Politically Exposed Person",
            )}
          >
            <Row
              label={t(
                "Cliente persona politicamente esposta (PEP)?",
                "Client politically exposed person (PEP)?",
              )}
              value={
                aml?.client?.is_pep === true
                  ? t("Sì", "Yes")
                  : aml?.client?.is_pep === false
                    ? "No"
                    : ""
              }
            />
            {aml?.client?.is_pep === true && (
              <Row
                label={t("Tipo di PEP", "PEP type")}
                value={aml?.client?.pep_role}
              />
            )}
          </SubSection>
        </Section>

        {/* ── ESECUTORE ── */}
        <Section
          title={t("Esecutore", "Executor")}
          goToStep={goToStep}
          step={2}
          isIta={isIta}
        >
          <Row
            label={t(
              "L'esecutore è diverso dal cliente?",
              "Executor different from the client?",
            )}
            value={
              aml?.has_executor === true
                ? t("Sì", "Yes")
                : aml?.has_executor === false
                  ? "No"
                  : ""
            }
          />

          {aml?.has_executor === true && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <SubSection
                title={t(
                  "Dati anagrafici e ruolo",
                  "Identification data and role",
                )}
              >
                <Row
                  label={t("Nome", "Name")}
                  value={aml?.executor?.first_name}
                />
                <Row
                  label={t("Cognome", "Lastname")}
                  value={aml?.executor?.last_name}
                />
                <Row
                  label={t("Luogo di nascita", "Place of birth")}
                  value={aml?.executor?.birth_place}
                />
                <Row
                  label={t("Data di nascita", "Date of birth")}
                  value={aml?.executor?.birth_date}
                />
                <Row
                  label={t("Codice fiscale", "Tax ID number")}
                  value={aml?.executor?.tax_code}
                />
                <Row label={t("Ruolo", "Role")} value={aml?.executor?.role} />
              </SubSection>

              <Divider sx={{ my: 1.5 }} />
              <SubSection title={t("Residenza", "Residence")}>
                <Row
                  label={t("Indirizzo", "Address")}
                  value={aml?.executor?.residence?.address}
                />
                <Row
                  label={t("CAP", "ZIP code")}
                  value={aml?.executor?.residence?.zip}
                />
                <Row
                  label={t("Città", "City")}
                  value={aml?.executor?.residence?.city}
                />
                <Row
                  label={t("Provincia", "Province")}
                  value={aml?.executor?.residence?.province}
                />
                <Row
                  label={t("Nazione", "Nation")}
                  value={aml?.executor?.residence?.country}
                />
              </SubSection>

              {aml?.executor?.domicile_same === false && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <SubSection title={t("Domicilio", "Domicile")}>
                    <Row
                      label={t("Indirizzo", "Address")}
                      value={aml?.executor?.domicile?.address}
                    />
                    <Row
                      label={t("CAP", "ZIP code")}
                      value={aml?.executor?.domicile?.zip}
                    />
                    <Row
                      label={t("Città", "City")}
                      value={aml?.executor?.domicile?.city}
                    />
                    <Row
                      label={t("Provincia", "Province")}
                      value={aml?.executor?.domicile?.province}
                    />
                    <Row
                      label={t("Nazione", "Nation")}
                      value={aml?.executor?.domicile?.country}
                    />
                  </SubSection>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />
              <SubSection
                title={t(
                  "Recapiti per la corrispondenza elettronica",
                  "Contact details for electronic correspondence",
                )}
              >
                <Row
                  label={t("Indirizzo PEC", "PEC address")}
                  value={aml?.executor?.pec}
                />
                <Row
                  label={t("Indirizzo e-mail", "E-mail address")}
                  value={aml?.executor?.email}
                />
              </SubSection>

              <Divider sx={{ my: 1.5 }} />
              <SubSection
                title={t(
                  "Estremi del documento di identificazione",
                  "Data of the identification document",
                )}
              >
                <Row
                  label={t("Tipo documento", "Document type")}
                  value={aml?.executor?.document?.type}
                />
                <Row
                  label={t("Numero documento", "Document number")}
                  value={aml?.executor?.document?.number}
                />
                <Row
                  label={t("Ente rilascio", "Issuing body")}
                  value={aml?.executor?.document?.issuer}
                />
                <Row
                  label={t("Data rilascio", "Issuing date")}
                  value={aml?.executor?.document?.issue_date}
                />
                <Row
                  label={t("Data scadenza", "Expiration date")}
                  value={aml?.executor?.document?.expiry_date}
                />
              </SubSection>

              <Divider sx={{ my: 1.5 }} />
              <SubSection
                title={t(
                  "Persona politicamente esposta",
                  "Politically Exposed Person",
                )}
              >
                <Row
                  label={t(
                    "Esecutore persona politicamente esposta (PEP)?",
                    "Executor politically exposed person (PEP)?",
                  )}
                  value={
                    aml?.executor?.is_pep === true
                      ? t("Sì", "Yes")
                      : aml?.executor?.is_pep === false
                        ? "No"
                        : ""
                  }
                />
                {aml?.executor?.is_pep === true && (
                  <Row
                    label={t("Tipo di PEP", "PEP type")}
                    value={aml?.executor?.pep_role}
                  />
                )}
              </SubSection>

              <Divider sx={{ my: 1.5 }} />
              <SubSection
                title={t(
                  "Documento attestante i poteri di rappresentanza",
                  "Document attesting the power to represent the client",
                )}
              >
                <Row
                  label={t("Titolo", "Title")}
                  value={aml?.executor?.representation_document}
                />
              </SubSection>
            </>
          )}
        </Section>

        {/* ── TITOLARI ── */}
        <Section
          title={t("Titolare effettivo", "Ultimate Beneficial Owner (UBO)")}
          goToStep={goToStep}
          step={3}
          isIta={isIta}
        >
          <Row
            label={t("Dichiarazione", "Declaration")}
            value={
              aml?.has_beneficial_owner === true
                ? t(
                    "Esistono uno o più titolari effettivi",
                    "There are one or more beneficial owners",
                  )
                : aml?.has_beneficial_owner === false
                  ? t(
                      "Confermo di agire in proprio, quindi non esiste un diverso titolare effettivo",
                      "I confirm that I am acting on my own behalf and that there is no other beneficial owner",
                    )
                  : ""
            }
          />

          {beneficialOwners.length > 0 && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              {beneficialOwners.map((owner, idx) => (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid #e8e8e8",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#f9f9f9",
                      borderBottom: "1px solid #e8e8e8",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1b2838",
                      }}
                    >
                      {t("Titolare", "Owner")} — {owner?.first_name}{" "}
                      {owner?.last_name}
                    </Typography>
                  </Box>

                  <Box sx={{ px: 2, py: 1.5 }}>
                    <SubSection
                      title={t(
                        "Dati anagrafici e ruolo",
                        "Identification data and role",
                      )}
                    >
                      <Row
                        label={t("Nome", "Name")}
                        value={owner?.first_name}
                      />
                      <Row
                        label={t("Cognome", "Lastname")}
                        value={owner?.last_name}
                      />
                      <Row
                        label={t("Luogo di nascita", "Place of birth")}
                        value={owner?.birth_place}
                      />
                      <Row
                        label={t("Data di nascita", "Date of birth")}
                        value={owner?.birth_date}
                      />
                      <Row
                        label={t("Codice fiscale", "Tax ID number")}
                        value={owner?.tax_code}
                      />
                      <Row
                        label={t("Professione/Attività", "Occupation/Activity")}
                        value={owner?.profession}
                      />
                    </SubSection>

                    {owner?.residence?.address && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <SubSection title={t("Residenza", "Residence")}>
                          <Row
                            label={t("Indirizzo", "Address")}
                            value={owner?.residence?.address}
                          />
                          <Row
                            label={t("CAP", "ZIP code")}
                            value={owner?.residence?.zip}
                          />
                          <Row
                            label={t("Città", "City")}
                            value={owner?.residence?.city}
                          />
                          <Row
                            label={t("Provincia", "Province")}
                            value={owner?.residence?.province}
                          />
                          <Row
                            label={t("Nazione", "Nation")}
                            value={owner?.residence?.country}
                          />
                        </SubSection>
                      </>
                    )}

                    {owner?.domicile_same === false &&
                      owner?.domicile?.address && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <SubSection title={t("Domicilio", "Domicile")}>
                            <Row
                              label={t("Indirizzo", "Address")}
                              value={owner?.domicile?.address}
                            />
                            <Row
                              label={t("CAP", "ZIP code")}
                              value={owner?.domicile?.zip}
                            />
                            <Row
                              label={t("Città", "City")}
                              value={owner?.domicile?.city}
                            />
                            <Row
                              label={t("Provincia", "Province")}
                              value={owner?.domicile?.province}
                            />
                            <Row
                              label={t("Nazione", "Nation")}
                              value={owner?.domicile?.country}
                            />
                          </SubSection>
                        </>
                      )}

                    {owner?.document?.type && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <SubSection
                          title={t(
                            "Estremi del documento di identificazione",
                            "Data of the identification document",
                          )}
                        >
                          <Row
                            label={t("Tipo documento", "Document type")}
                            value={owner?.document?.type}
                          />
                          <Row
                            label={t("Numero documento", "Document number")}
                            value={owner?.document?.number}
                          />
                          <Row
                            label={t("Ente rilascio", "Issuing body")}
                            value={owner?.document?.issuer}
                          />
                          <Row
                            label={t("Data rilascio", "Issuing date")}
                            value={owner?.document?.issue_date}
                          />
                          <Row
                            label={t("Data scadenza", "Expiration date")}
                            value={owner?.document?.expiry_date}
                          />
                        </SubSection>
                      </>
                    )}

                    <Divider sx={{ my: 1 }} />
                    <Row
                      label={t(
                        "Ragione cui il soggetto si qualifica quale titolare effettivo",
                        "Reason why this person qualifies as UBO of the client",
                      )}
                      value={owner?.qualification_reason}
                    />

                    <Divider sx={{ my: 1 }} />
                    <SubSection
                      title={t(
                        "Persona politicamente esposta",
                        "Politically Exposed Person",
                      )}
                    >
                      <Row
                        label={t(
                          "Titolare effettivo persona politicamente esposta (PEP)?",
                          "UBO politically exposed person (PEP)?",
                        )}
                        value={
                          owner?.is_pep === true
                            ? t("Sì", "Yes")
                            : owner?.is_pep === false
                              ? "No"
                              : ""
                        }
                      />
                      {owner?.is_pep === true && (
                        <Row
                          label={t("Tipo di PEP", "PEP type")}
                          value={owner?.pep_role}
                        />
                      )}
                    </SubSection>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Section>

        {/* ── PRESTAZIONE ── */}
        <Section
          title={t("Prestazione", "Services")}
          goToStep={goToStep}
          step={4}
          isIta={isIta}
        >
          <Row
            label={t(
              "Natura e scopo della prestazione professionale richiesta allo Studio LEXIA",
              "Nature and purpose of the professional mandate required to LEXIA firm",
            )}
            value={aml?.service?.nature === "other" ? aml?.service?.nature_other : aml?.service?.nature}
          />
          <Row
            label={t(
              "Mezzo di pagamento utilizzato per il pagamento dell'operazione",
              "Payment instrument used for the payment of the transaction",
            )}
            value={aml?.service?.payment_method}
          />
          <Row
            label={t(
              "Fonte/i di provenienza dei fondi impiegati per l'operazione",
              "Source(s) of funds used for the transaction",
            )}
            value={aml?.service?.funds_source === "other" ? aml?.service?.funds_source_other : aml?.service?.funds_source}
          />
        </Section>
      </Grid>
    </Grid>
  );
};

export default Summary_Aml_Fisico;
